// PageDescription: 지역별 비급여 가격비교 페이지
import { dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React, { useMemo } from 'react';

import { ExtendedQueryClient } from '@/models/common/query-client';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import { TREATMENT_CATEGORIES_IDS } from '@/constants/category';
import {
  NonBenefitHomeQueries,
  TREATMENT_CATEGORIES,
  useNonBenefitMedicinesForSeo,
  useNonBenefitPriceTotalSummary,
  usePharmacyMedicinePriceTotalSummary,
  useSearchDoctorsForSeoByUser,
} from '@/entities/non-benefit';
import useNearbyPharmacyV3ByRegionCode from '@/entities/non-benefit/hooks/useNearbyPharmacyV3ByRegionCode';

import useAppInstallTopBanner from '@/hooks/cuj/useAppInstallTopBanner';
import useGetKeywordSearchMagazineInfinite from '@/hooks/search/magazine/useGetKeywordSearchMagazineInfinite';
import useGetKeywordQuestionCardInfinite from '@/hooks/search/qna/useGetKeywordQuestionCardInfinite';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { EDiagnosisContactType } from '@/types/diagnosis';
import { nonBenefitSeoLdJson } from '@/utils/format';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import { addQueryString } from '@/utils/url';
import { getAdditionalMetaTags, getDescriptionForMetaTag, useNonBenefitHomeViewModel } from '@/views/non-benefit';
import NonBenefitHomeView from '@/views/non-benefit/ui/NonBenefitHomeView';

import useGetSearchKoreaAddressHierarchy from '@/entities/non-benefit/hooks/useGetSearchKoreaAddressHierarchy';
import useGetBannerAdExchange from '@/hooks/advertisement/useGetBannerAdExchange';
import useUnifiedAdBanner from '@/hooks/advertisement/useUnifiedAdBanner';
import { AdPageType } from '@/models/ad/list';
import { ssrQueryCache } from '@/models/common/ssr-cache';
import { useRouter } from 'next/router';

const NonBenefitHomePages = ({ client }: CustomPageProps) => {
  const viewModel = useNonBenefitHomeViewModel(client);
  const topBannerViewModel = useAppInstallTopBanner();
  const router = useRouter();
  const { treatmentId } = viewModel.queries as NonBenefitHomeQueries;

  const title = `${viewModel.selectedAddressTitle} ${viewModel.selectedMedicine?.medicineDescription} 처방 최저가 병원/약국 가격(${new Date().getFullYear()}) - 닥터나우`;
  const description = getDescriptionForMetaTag(treatmentId);
  const canonical = `${process.env.NEXT_PUBLIC_DOMAIN}${router.asPath}`;

  const nonBenefitLdJson = useMemo(() => {
    return nonBenefitSeoLdJson({
      doctors: viewModel.nonBenefitDoctors,
      address: viewModel.selectedAddressTitle,
      faqs: viewModel.faqs,
      canonical,
    });
  }, [viewModel.nonBenefitDoctors, viewModel.selectedAddressTitle, viewModel.faqs, canonical]);

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        openGraph={{
          title,
          description: description,
          url: canonical,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_MAIN_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
        additionalMetaTags={getAdditionalMetaTags(treatmentId)}
      />
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(nonBenefitLdJson) }} />
      </Head>
      <NonBenefitHomeView client={client} viewModel={viewModel} topBannerViewModel={topBannerViewModel} />
    </>
  );
};

NonBenefitHomePages.Layout = DefaultLayout;
export default NonBenefitHomePages;

const SEOUL_REGION_CODE = '11';
const HIERARCHY_CACHE_KEY = 'hierarchy_queryClient_cache';
const DEFAULT_CACHE_EXPIRATION = Infinity;

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (ctx) => {
  const queryClient = new ExtendedQueryClient();
  const queries = ctx.query as NonBenefitHomeQueries;

  /* 쿼리가 없는 경우 기본 데이터로 리다이렉트 */
  if (!queries.treatmentId || !queries.regionCode) {
    return {
      redirect: {
        permanent: true,
        destination:
          ROUTES.NON_BENEFIT.HOME.path +
          addQueryString({
            treatmentId: queries.treatmentId ?? TREATMENT_CATEGORIES_IDS.Hairloss,
            regionCode: queries.regionCode ?? SEOUL_REGION_CODE,
          }),
      },
    };
  }

  const contactType =
    TREATMENT_CATEGORIES.find((category) => category.id === queries.treatmentId)?.contactType ??
    EDiagnosisContactType.NonF2F;

  // 1단계: 핵심 데이터와 주소 계층구조를 병렬로 로딩 (캐시 적용)
  const [nonBenefitMedicines, _hierarchyResult] = await Promise.all([
    useNonBenefitMedicinesForSeo.fetchQuery(queryClient, {
      treatmentId: queries.treatmentId,
      contactType,
    }),

    (async () => {
      const hierarchyCacheKey = useGetSearchKoreaAddressHierarchy.getKey();

      if (ssrQueryCache.has(HIERARCHY_CACHE_KEY)) {
        const cachedData = ssrQueryCache.get(HIERARCHY_CACHE_KEY);
        queryClient.setQueryDataFromCache(cachedData ?? []);

        const queryData = queryClient.getQueryData(hierarchyCacheKey);
        if (queryData) {
          return queryData;
        }
      }

      try {
        const result = await useGetSearchKoreaAddressHierarchy.fetchQuery(queryClient);

        ssrQueryCache.set(
          HIERARCHY_CACHE_KEY,
          queryClient.getQueryClientData([hierarchyCacheKey]),
          DEFAULT_CACHE_EXPIRATION
        );
        return result;
      } catch {
        return null;
      }
    })(),
  ]);

  const selectedMedicine = nonBenefitMedicines.findMedicineByTreatmentMedicineUnitId(queries.treatmentMedicineUnitId);
  const treatmentMedicineId = selectedMedicine?.id || nonBenefitMedicines.medicines[0]?.id;

  /* 색인 이슈로 잘못된 treatmentMedicineUnitId가 유입되고 있어 매칭되는 medicine이 없으면 리다이렉트 */
  const medicine = nonBenefitMedicines.findMedicineByQueryTreatmentMedicineUnitId(queries.treatmentMedicineUnitId);

  if (!medicine && nonBenefitMedicines.medicines.length > 0) {
    const defaultMedicine = nonBenefitMedicines.medicines[0];
    const treatmentMedicineUnitId = defaultMedicine.units[0]?.id;

    if (treatmentMedicineUnitId) {
      return {
        redirect: {
          permanent: true,
          destination:
            ROUTES.NON_BENEFIT.HOME.path +
            addQueryString({
              treatmentId: queries.treatmentId,
              treatmentMedicineUnitId: treatmentMedicineUnitId,
              regionCode: queries.regionCode,
            }),
        },
      };
    }
  }
  // 3단계: dispensationUnit 계산 최적화 및 디버깅
  const medicineUnit = nonBenefitMedicines.findMedicineUnitByTreatmentMedicineUnitId(queries.treatmentMedicineUnitId);
  const dispensationUnitModel = nonBenefitMedicines?.findDispensationUnitByTreatmentMedicineUnitId(
    queries.treatmentMedicineUnitId,
    medicineUnit
  );
  const dispensationUnit = dispensationUnitModel?.getDispensationUnit() ?? 0;

  // 4단계: 모든 필수 데이터를 병렬로 프리패치 (성능 최적화)
  const prefetchPromises = [
    useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.TopCtaBand, ctx.client),
    useUnifiedAdBanner.prefetchQuery(queryClient, AdPageType.TopCommonBanner, ctx.client),
    // 핵심 가격 정보
    useNonBenefitPriceTotalSummary.prefetchQuery(
      queryClient,
      treatmentMedicineId ?? '',
      queries.regionCode ?? '',
      contactType
    ),

    usePharmacyMedicinePriceTotalSummary.prefetchQuery(
      queryClient,
      treatmentMedicineId ?? '',
      queries.regionCode ?? '',
      contactType
    ),

    // 의사 목록
    useSearchDoctorsForSeoByUser.prefetchQuery(
      queryClient,
      useSearchDoctorsForSeoByUser.getNonBenefitHomeParams(
        queries.regionCode ?? '',
        queries.treatmentMedicineUnitId ?? ''
      ),
      contactType
    ),

    // 매거진 데이터
    useGetKeywordSearchMagazineInfinite.prefetchQuery(
      queryClient,
      useGetKeywordSearchMagazineInfinite.getNonBenefitHomeArticleParams(selectedMedicine?.medicineDescription ?? '')
    ),

    // QnA 데이터
    useGetKeywordQuestionCardInfinite.prefetchQuery(queryClient, {
      keyword: selectedMedicine?.medicineDescription ?? '',
    }),
  ];

  // 약국 데이터 prefetch
  const pharmacyPrefetchPromise = (async () => {
    try {
      if (!selectedMedicine?.treatment?.id || !selectedMedicine?.id || !queries.regionCode) {
        return;
      }

      const pharmacyParams = {
        regionCode: queries.regionCode,
        treatmentId: selectedMedicine.treatment.id,
        treatmentMedicineId: selectedMedicine.id,
        dispensationUnit,
      };

      await useNearbyPharmacyV3ByRegionCode.prefetchQuery(queryClient, pharmacyParams);
    } catch {
      // 에러 발생해도 다른 prefetch는 계속 진행
    }
  })();

  await Promise.all([...prefetchPromises, pharmacyPrefetchPromise]);

  const dehydratedState = dehydrate(queryClient);

  return {
    props: {
      queries,
      dehydratedState: JSON.parse(JSON.stringify(dehydratedState)),
    },
  };
});
